// src/components/AssignMemberSelect/AssignMemberSelect.jsx
//
// "Assign member" field for Create Stage and Create Task.
//
// Rules (used by the UI and by the submit handlers). Permissions come from
// config/roles so there is one source of truth:
//   - Only roles with TASK_ASSIGN / TODO_ASSIGN (Creator, Approver) may pick
//     another member. Everyone else gets no field at all.
//   - Only members of the current project are offered.
//   - isForbiddenAssignment() is honoured: never Client/Viewer, and a Creator
//     can't assign a stage ("task") to an Approver.
//   - Nothing chosen -> the logged-in user becomes the owner.
//   - Something chosen -> that member becomes the owner.
//
// `scope` is "task" (a stage, in backend terms) or "todo" (a task in the UI).

import React, { useMemo } from "react";
import { can, PERM, isForbiddenAssignment } from "../../config/roles";
import "./AssignMemberSelect.scss";

export const canAssignToOthers = (role, scope = "task") =>
  can(role, scope === "todo" ? PERM.TODO_ASSIGN : PERM.TASK_ASSIGN);

const isAssignableMember = (member, role, scope) =>
  !isForbiddenAssignment(role, member?.roleType ?? member?.roleId, scope);

/**
 * Decide who ends up owning the new stage/task.
 * Call this in the submit handler instead of trusting the form state, so a
 * Doer (or a stale selection) can never assign to someone else.
 *
 * @returns {number|null} the empId to send as taskOwnerId / assignedEmpId
 */
export const resolveAssigneeId = ({
  selectedEmpId,
  currentEmpId,
  members = [],
  currentUserRole,
  scope = "task",
}) => {
  const self = currentEmpId != null ? Number(currentEmpId) : null;

  if (!canAssignToOthers(currentUserRole, scope)) return self;
  if (selectedEmpId === "" || selectedEmpId == null) return self;

  const target = (members || []).find(
    (m) => String(m.empId) === String(selectedEmpId),
  );
  if (!target) return self; // not a member of this project
  if (!isAssignableMember(target, currentUserRole, scope)) return self;
  return Number(selectedEmpId);
};

export default function AssignMemberSelect({
  id = "assign-member",
  label = "Assign to",
  showLabel = true, // false for compact/table use; label stays as aria-label
  members = [],
  value = "",
  onChange,
  currentEmpId,
  currentUserRole,
  scope = "task",
  disabled = false,
}) {
  // Others only; the logged-in user is the "nobody chosen" default below.
  const options = useMemo(
    () =>
      (members || [])
        .filter((m) => String(m.empId) !== String(currentEmpId))
        .filter((m) => isAssignableMember(m, currentUserRole, scope))
        .sort((a, b) => String(a.empName).localeCompare(String(b.empName))),
    [members, currentEmpId, currentUserRole, scope],
  );

  const me = (members || []).find(
    (m) => String(m.empId) === String(currentEmpId),
  );

  if (!canAssignToOthers(currentUserRole, scope)) return null;

  return (
    <div className="assign-member">
      {showLabel && (
        <label htmlFor={id} className="assign-member__label">
          {label}
        </label>
      )}
      <select
        id={id}
        aria-label={showLabel ? undefined : label}
        className="assign-member__select"
        value={value ?? ""}
        disabled={disabled}
        onChange={(e) => onChange?.(e.target.value)}
      >
        <option value="">{me?.empName ? `Me (${me.empName})` : "Me"}</option>
        {options.map((m) => (
          <option key={m.empId} value={m.empId}>
            {m.empName}
            {m.roleType ? ` — ${String(m.roleType).toLowerCase()}` : ""}
          </option>
        ))}
      </select>
    </div>
  );
}
